import { ApiError } from "@circle-react/config/CustomErrors";

export const CHECKOUT_SESSION_OWNERSHIP_TOKEN_HEADER =
  "X-Checkout-Session-Ownership-Token" as const;
export const CHECKOUT_SESSION_OWNERSHIP_PROTOCOL_HEADER =
  "X-Checkout-Session-Ownership-Protocol" as const;
export const CHECKOUT_SESSION_OWNERSHIP_PROTOCOL_VERSION = "1" as const;
const CHECKOUT_CLIENT_REFRESH_REQUIRED =
  "checkout_client_refresh_required" as const;

export type CheckoutSessionOwnershipToken = string;

const ownershipTokenHeader = [CHECKOUT_SESSION_OWNERSHIP_TOKEN_HEADER] as const;
const CHECKOUT_CLIENT_REFRESH_QUERY_PARAM = "checkout_client_refresh";

interface CheckoutSessionOwnershipRequestParams {
  ownershipToken: CheckoutSessionOwnershipToken | null | undefined;
  sessionId: number | null | undefined;
  targetSessionId?: number | null;
}

export const checkoutSessionOwnershipRequestOptions = ({
  ownershipToken,
  sessionId,
  targetSessionId = sessionId,
}: CheckoutSessionOwnershipRequestParams) => ({
  headers:
    ownershipToken && sessionId != null && targetSessionId === sessionId
      ? { [CHECKOUT_SESSION_OWNERSHIP_TOKEN_HEADER]: ownershipToken }
      : undefined,
  redirect:
    ownershipToken && sessionId != null && targetSessionId === sessionId
      ? ("error" as const)
      : undefined,
  sameOriginHeaders: ownershipTokenHeader,
  redactRequestHeaders: ownershipTokenHeader,
});

export const checkoutOwnershipOptionsForTarget = (
  ownershipToken: CheckoutSessionOwnershipToken | null | undefined,
  sessionId: number | null | undefined,
  targetSessionId: number | null | undefined,
) =>
  checkoutSessionOwnershipRequestOptions({
    ownershipToken,
    sessionId,
    targetSessionId,
  });

export const checkoutSessionOwnershipCreationOptions = () => ({
  headers: {
    [CHECKOUT_SESSION_OWNERSHIP_PROTOCOL_HEADER]:
      CHECKOUT_SESSION_OWNERSHIP_PROTOCOL_VERSION,
  },
  sameOriginHeaders: [CHECKOUT_SESSION_OWNERSHIP_PROTOCOL_HEADER] as const,
  redactRequestHeaders: ownershipTokenHeader,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object";

export const isCheckoutClientRefreshRequired = (
  error: unknown,
): error is ApiError =>
  error instanceof ApiError &&
  error.status === 409 &&
  isRecord(error.body) &&
  error.body.error === CHECKOUT_CLIENT_REFRESH_REQUIRED;

export class CheckoutSessionInitializationError extends Error {
  readonly canRetryWithFreshSession: boolean;
  readonly rejectedSessionId: number | undefined;

  constructor({
    canRetryWithFreshSession = false,
    rejectedSessionId,
  }: {
    canRetryWithFreshSession?: boolean;
    rejectedSessionId?: number;
  } = {}) {
    super("Checkout could not verify the session ownership protocol.");
    this.name = "CheckoutSessionInitializationError";
    this.canRetryWithFreshSession = canRetryWithFreshSession;
    this.rejectedSessionId = rejectedSessionId;
  }
}

class CheckoutClientRefreshRequiredError extends Error {
  constructor() {
    super("Checkout must reload before it can start.");
    this.name = "CheckoutClientRefreshRequiredError";
  }
}

export const isTerminalCheckoutSessionCreationError = (
  error: unknown,
): boolean =>
  error instanceof CheckoutSessionInitializationError ||
  error instanceof CheckoutClientRefreshRequiredError ||
  (error instanceof ApiError && error.status === 409);

export const getCheckoutSessionReferrer = (): string => {
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.delete(CHECKOUT_CLIENT_REFRESH_QUERY_PARAM);
  return currentUrl.href;
};

export const clearCheckoutClientRefreshAttempt = (): void => {
  const currentUrl = new URL(window.location.href);
  if (!currentUrl.searchParams.has(CHECKOUT_CLIENT_REFRESH_QUERY_PARAM)) return;

  currentUrl.searchParams.delete(CHECKOUT_CLIENT_REFRESH_QUERY_PARAM);
  window.history.replaceState(
    window.history.state,
    "",
    `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
  );
};

export const reloadCheckoutOnceForClientRefresh = (): never => {
  const currentUrl = new URL(window.location.href);
  if (
    currentUrl.searchParams.get(CHECKOUT_CLIENT_REFRESH_QUERY_PARAM) === "1"
  ) {
    clearCheckoutClientRefreshAttempt();
    throw new CheckoutClientRefreshRequiredError();
  }

  currentUrl.searchParams.set(CHECKOUT_CLIENT_REFRESH_QUERY_PARAM, "1");
  window.location.replace(currentUrl.href);
  throw new CheckoutClientRefreshRequiredError();
};
